import { ReactNode, memo } from 'react';
import { FormControl, InputBaseProps, InputBase, InputLabel, FormHelperText, styled } from '@mui/material';
import { ControllerRenderProps, FieldValues, useController } from 'react-hook-form';
import { Country } from '@xendit/id-open-banking-ekyb-common/dist/enums/submission';
import { CountryMap } from 'helpers/utils/enumsNameMappings';
import { t } from 'i18next';

interface Props extends Omit<InputBaseProps, 'label'> {
  label: string | ReactNode;
  hookForm?: ControllerRenderProps<FieldValues, string>;
  isConditional?: boolean;
}

const TextInput = memo(
  ({ label, hookForm, isConditional, ...props }: Props) => {
    const { fieldState, field } = useController({ name: hookForm?.name ?? '' });
    const isIndonesia = field.value === Country.ID;

    return (
      <FormControl
        sx={{ pt: isConditional ? 0 : 1, pb: 1, mb: 1 }}
        fullWidth={props.fullWidth}
        error={Boolean(fieldState.error?.message)}
      >
        {label && (
          <InputLabel shrink htmlFor={props.name}>
            {label}
          </InputLabel>
        )}
        <InputBaseStyled
          id={props.name}
          {...props}
          {...hookForm}
          sx={{ pt: isConditional ? 0 : 1 }}
          onChange={(e) => {
            if (props.type === 'number') {
              return hookForm?.onChange(parseInt(e.target.value, 10));
            }
            return hookForm?.onChange(e.target.value);
          }}
          value={isIndonesia && props.disabled ? t(CountryMap[Country.ID]) : hookForm?.value}
        />
        <FormHelperText sx={{ m: 0 }}>{fieldState.error?.message}</FormHelperText>
      </FormControl>
    );
  },
  (prevProps, nextProps) => prevProps.hookForm?.value === nextProps.hookForm?.value
);

TextInput.displayName = 'TextInput';

const InputBaseStyled = styled(InputBase)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  '& input.Mui-disabled': {
    color: theme.palette.text.primary,
    textFillColor: theme.palette.text.primary
  }
}));

export default TextInput;
